import { lazy } from 'react';

import { isPendingUpdate } from './sw-update';

const TAG = '[lazyWithRetry]';
const MAX_RELOADS = 2;

/**
 * Wraps React.lazy() with import retry + cache-busting page reload.
 * Customer NEVER sees an error — worst case is a couple page reloads.
 *
 * After a deploy, Vite generates new chunk hashes. Two failure modes:
 * 1. Stale index.html → references deleted chunks → 404
 * 2. Fresh index.html but chunk not yet at CDN edge → transient 404
 *
 * Strategy (2 layers, no throw):
 * Layer 1: Try import once — if version update pending, reload immediately
 * Layer 2: Cache-busting page reload (up to 2x) — handles stale HTML
 * Layer 3: Redirect to / with clean state — nuclear reset, user lands on home page
 *
 * Per-chunk sessionStorage tracks reload count to prevent infinite loops.
 */
export function lazyWithRetry<T extends React.ComponentType<React.ComponentProps<T>>>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    // Extract import path — the only stable part after Vite minification.
    // e.g. ()=>Jt(()=>import("./ScreensPage-Cmv-Q3A4.js").then(...) → "./ScreensPage-Cmv-Q3A4.js"
    const chunk = importFn.toString().match(/import\(["']([^"']+)["']\)/)?.[1] ?? 'unknown';
    const storageKey = `lazyRetry:${chunk}`;
    const reloadCount = parseInt(sessionStorage.getItem(storageKey) || '0', 10);

    try {
      const module = await importFn();
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      console.warn(`${TAG} ${chunk} import failed`, error);

      // If a new version is pending, chunks are gone from CDN — reload immediately
      if (isPendingUpdate()) {
        console.warn(`${TAG} ${chunk} version update pending, reloading immediately`);
        window.location.reload();
        return new Promise(() => {});
      }

      // Cache-busting page reload (up to MAX_RELOADS times)
      if (reloadCount < MAX_RELOADS) {
        console.warn(`${TAG} ${chunk} reloading (#${reloadCount + 1})`);
        sessionStorage.setItem(storageKey, String(reloadCount + 1));
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', Date.now().toString());
        window.location.replace(url.toString());
        return new Promise(() => {});
      }

      // All reloads exhausted — redirect to home
      console.warn(`${TAG} ${chunk} ${MAX_RELOADS} reloads exhausted, redirecting to /`);
      for (let j = sessionStorage.length - 1; j >= 0; j--) {
        const key = sessionStorage.key(j);
        if (key?.startsWith('lazyRetry:')) sessionStorage.removeItem(key);
      }
      window.location.replace('/');
      return new Promise(() => {});
    }
  });
}
